import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from "@angular/core";
import {
  ClientNoteViewModel,
  ClientNoteUpdate,
  CLIENT_NOTE_TYPES,
  SCHEDULED_OUTAGE_NOTE_TYPE,
  RESOURCE_REQUEST_NOTE_TYPE,
  RESOURCE_REQUEST_STATUSES,
} from "@app/shared/models/clients/ClientNoteViewModel.model";
import { HttpClient } from "@app/shared/services/http/http-client.service";
import { clientService } from "@app/client/shared/client.service";
import { DriverService } from "@app/driver/shared/driver.service";
import { ConfigService } from "@app/utilities/config/shared/config.service";
import { parseDateOnly, toDateOnlyString } from "@app/shared/utils/date-only.util";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: "app-client-note-view-modal",
  templateUrl: "./client-note-view-modal.component.html",
  styleUrls: ["./client-note-view-modal.component.css"],
})
export class ClientNoteViewModalComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() note: ClientNoteViewModel | null = null;
  @Input() canEdit: boolean = false;
  @Input() clients: any[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();
  @Output() noteSaved = new EventEmitter<ClientNoteViewModel>();

  displayNote: ClientNoteViewModel | null = null;
  saving = false;

  // ── Status updates (every saved note can carry them) ──────────────
  showAddUpdate = false;
  isSavingUpdate = false;
  updateAttemptedSubmit = false;
  addUpdate: { label?: string; internalNotes?: string; clientNotes?: string } = {};
  /** When set, the update form is editing this existing update instead of adding one. */
  editingUpdateId: string | null = null;
  /** Set while the compose dialog is emailing a single update. */
  private emailingUpdateId: string | null = null;

  /** Email-to-client compose dialog state. */
  showEmailCompose = false;
  emailSending = false;
  emailValidationError = "";
  emailPreview = { to: "", ccEmails: "", subject: "", body: "" };
  driverNotesCcEmail: string = "";

  // ── Sender signature (added to every email this modal sends) ──────
  userEmailSignature = "";
  showSignatureEditor = false;
  signatureEditText = "";
  savingSignature = false;
  signatureSaveMessage = "";
  signatureSaveError = false;
  /** The message without the signature, so the signature editor can re-render the body. */
  private emailBaseBody = "";
  /** Set once the signature has been requested. */
  private signatureLoaded = false;
  /** Set once the configured default CC has been requested. */
  private defaultCcLoaded = false;

  readonly noteTypes = [...CLIENT_NOTE_TYPES];
  readonly noteStatuses = ["Open", "Resolved", "Closed"];

  readonly scheduledOutageType = SCHEDULED_OUTAGE_NOTE_TYPE;
  readonly resourceRequestType = RESOURCE_REQUEST_NOTE_TYPE;
  readonly resourceRequestStatusOptions = [...RESOURCE_REQUEST_STATUSES];

  /** Exposed for the template so outage dates render as the stored calendar day. */
  toDisplayDate = parseDateOnly;

  /** The selected client's scheduling groups, loaded on demand for scheduled outages. */
  schedulingTeamsForClient: any[] = [];
  schedulingTeamsLoading = false;
  private schedulingTeamsClientId: string | null = null;

  /** Scheduling groups the outage in the form applies to. */
  outageTeamIds = new Set<string>();

  constructor(
    private http: HttpClient,
    private clientServiceRef: clientService,
    private driverService: DriverService,
    private configService: ConfigService,
    private toastr: ToastrService
  ) {}

  isScheduledOutage(note: { type?: string } | null | undefined): boolean {
    return note?.type === this.scheduledOutageType;
  }

  isResourceRequest(note: { type?: string } | null | undefined): boolean {
    return note?.type === this.resourceRequestType;
  }

  /**
   * Only saved General notes can be emailed to the client - the same rule the client
   * profile's notes tab uses.
   */
  canEmailClient(): boolean {
    return (
      !!this.displayNote?.id && !!this.displayNote?.clientId && this.displayNote?.type === "General"
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["visible"] && this.visible) {
      this.loadEmailSignature();
      this.loadDefaultCc();
      this.displayNote = this.note ? { ...this.note } : null;

      // p-calendar needs real Date objects even when the note arrived as an ISO string.
      if (this.displayNote) {
        this.displayNote.startDate = parseDateOnly(this.displayNote.startDate);
        this.displayNote.endDate = parseDateOnly(this.displayNote.endDate);
        this.displayNote.followUpDate = this.toDateOrNull(this.displayNote.followUpDate);
      }

      this.outageTeamIds = new Set<string>(this.displayNote?.schedulingTeamIds || []);
      this.resetUpdateState();
      this.sortUpdates();
      this.loadNoteUpdates();

      if (this.isScheduledOutage(this.displayNote)) {
        this.loadSchedulingTeams(this.displayNote?.clientId || "");
      }
    }
  }

  /** Converts a stored ISO date string into a Date for p-calendar (null when unset/invalid). */
  private toDateOrNull(value: Date | string | null | undefined): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * A scheduled outage needs a date range and at least one group; picking another type drops
   * those fields, and picking the outage type defaults a new one to a two day window from today.
   */
  onNoteTypeChange(): void {
    if (!this.displayNote) return;

    // Resource request notes carry a fulfillment status; drop it for every other type.
    if (!this.isResourceRequest(this.displayNote)) {
      this.displayNote.resourceRequestStatus = null;
    }

    if (!this.isScheduledOutage(this.displayNote)) {
      this.displayNote.startDate = null;
      this.displayNote.endDate = null;
      this.outageTeamIds.clear();
      return;
    }

    if (!this.displayNote.startDate) {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      this.displayNote.startDate = start;
      this.displayNote.endDate = end;
    }

    this.loadSchedulingTeams(this.displayNote?.clientId || "");
  }

  /** The client drives which scheduling groups can be picked, so reload them when it changes. */
  onClientChange(): void {
    this.outageTeamIds.clear();

    if (this.isScheduledOutage(this.displayNote)) {
      this.loadSchedulingTeams(this.displayNote?.clientId || "");
    }
  }

  loadSchedulingTeams(clientId: string): void {
    if (!clientId) {
      this.schedulingTeamsForClient = [];
      this.schedulingTeamsClientId = null;
      return;
    }

    if (this.schedulingTeamsClientId === clientId) return;

    this.schedulingTeamsClientId = clientId;
    this.schedulingTeamsLoading = true;
    this.schedulingTeamsForClient = [];

    this.driverService.getSchedulingTeamsByClient(clientId).subscribe({
      next: (teams: any[]) => {
        if (this.schedulingTeamsClientId !== clientId) return;
        this.schedulingTeamsForClient = teams || [];
        this.schedulingTeamsLoading = false;
      },
      error: () => {
        if (this.schedulingTeamsClientId !== clientId) return;
        this.schedulingTeamsClientId = null;
        this.schedulingTeamsLoading = false;
        this.toastr.error("Failed to load scheduling groups.");
      },
    });
  }

  toggleOutageTeam(teamId: string): void {
    if (this.outageTeamIds.has(teamId)) this.outageTeamIds.delete(teamId);
    else this.outageTeamIds.add(teamId);
  }

  isOutageTeamSelected(teamId: string): boolean {
    return this.outageTeamIds.has(teamId);
  }

  get allOutageTeamsSelected(): boolean {
    return (
      this.schedulingTeamsForClient.length > 0 &&
      this.outageTeamIds.size === this.schedulingTeamsForClient.length
    );
  }

  /** True when only some groups are picked, so the select-all box renders as indeterminate. */
  get someOutageTeamsSelected(): boolean {
    return (
      this.outageTeamIds.size > 0 && this.outageTeamIds.size < this.schedulingTeamsForClient.length
    );
  }

  toggleAllOutageTeams(event: Event): void {
    const selectAll = (event.target as HTMLInputElement).checked;

    this.outageTeamIds.clear();
    if (selectAll) this.schedulingTeamsForClient.forEach((team) => this.outageTeamIds.add(team.id));
  }

  /** Names of the groups the outage targets, for the read-only view. */
  get outageGroupNames(): string {
    return Array.from(this.outageTeamIds)
      .map((id) => this.schedulingTeamsForClient.find((t) => t.id === id)?.name || "Unknown group")
      .join(", ");
  }

  closeModal() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.closed.emit();
  }

  saveNote() {
    if (!this.displayNote || !this.displayNote.note?.trim()) {
      this.toastr.warning("Note content is required");
      return;
    }

    if (!this.displayNote.clientId) {
      this.toastr.warning("Client is required");
      return;
    }

    if (this.isResourceRequest(this.displayNote) && !this.displayNote.resourceRequestStatus) {
      this.toastr.warning("Resource request status is required");
      return;
    }

    if (this.isScheduledOutage(this.displayNote)) {
      if (!this.displayNote.startDate || !this.displayNote.endDate) {
        this.toastr.warning("An outage needs a start and end date");
        return;
      }
      if (
        new Date(this.displayNote.endDate).getTime() <
        new Date(this.displayNote.startDate).getTime()
      ) {
        this.toastr.warning("The outage end date must be on or after the start date");
        return;
      }
      if (this.outageTeamIds.size === 0) {
        this.toastr.warning("Select at least one scheduling group for the outage");
        return;
      }
    }

    // Dates travel as calendar days (yyyy-MM-dd) so the outage lands on the picked days regardless
    // of the browser timezone; the profile notes tab does the same.
    const isOutage = this.isScheduledOutage(this.displayNote);

    const payload: ClientNoteViewModel = {
      ...this.displayNote,
      startDate: isOutage ? toDateOnlyString(this.displayNote.startDate) : null,
      endDate: isOutage ? toDateOnlyString(this.displayNote.endDate) : null,
      schedulingTeamIds: isOutage ? Array.from(this.outageTeamIds) : [],
    };

    this.saving = true;
    this.savePendingUpdateThenSave(payload);
  }

  /**
   * Saves any status update still open in the add/edit form first, then the note itself.
   * Updates are children of the note, so a failed update save aborts the note save.
   */
  private savePendingUpdateThenSave(payload: ClientNoteViewModel): void {
    this.savePendingUpdate().then((ok) => {
      if (!ok) {
        this.saving = false;
        return;
      }

      this.http.post("api/client/notes/save", payload).subscribe({
        next: (saved: any) => {
          this.saving = false;
          this.toastr.success("Note saved");
          this.noteSaved.emit(saved);
          this.closeModal();
        },
        error: () => {
          this.saving = false;
          this.toastr.error("Error saving note");
        },
      });
    });
  }

  // ── Status updates ────────────────────────────────────────────────

  get hasUpdates(): boolean {
    return Array.isArray(this.displayNote?.updates) && (this.displayNote?.updates?.length ?? 0) > 0;
  }

  private resetUpdateState(): void {
    this.showAddUpdate = false;
    this.isSavingUpdate = false;
    this.updateAttemptedSubmit = false;
    this.addUpdate = {};
    this.editingUpdateId = null;
    this.emailingUpdateId = null;
  }

  /** Keep the timeline oldest-first (ascending by created date). */
  private sortUpdates(): void {
    const updates = this.displayNote?.updates;
    if (!Array.isArray(updates)) return;
    updates.sort((a, b) => {
      const at = a.dateCreatedUtc ? new Date(a.dateCreatedUtc as any).getTime() : 0;
      const bt = b.dateCreatedUtc ? new Date(b.dateCreatedUtc as any).getTime() : 0;
      return at - bt;
    });
  }

  /** Loads the note's updates when the caller passed a note from a list projection (no updates). */
  private loadNoteUpdates(): void {
    if (!this.displayNote?.id) return;
    if (Array.isArray(this.displayNote.updates)) return;

    this.http.get(`api/client/notes/${this.displayNote.id}`).subscribe({
      next: (full: any) => {
        if (full && Array.isArray(full.updates) && this.displayNote) {
          this.displayNote.updates = full.updates;
          this.sortUpdates();
        }
      },
      error: () => {},
    });
  }

  openAddUpdate(): void {
    this.showAddUpdate = true;
    this.updateAttemptedSubmit = false;
    this.editingUpdateId = null;
    this.addUpdate = {};
  }

  startEditUpdate(update: ClientNoteUpdate): void {
    this.editingUpdateId = update.id ?? null;
    this.showAddUpdate = true;
    this.updateAttemptedSubmit = false;
    this.addUpdate = {
      label: update.label || "",
      internalNotes: update.internalNotes || "",
      clientNotes: update.clientNotes || "",
    };
  }

  cancelAddUpdate(): void {
    this.showAddUpdate = false;
    this.updateAttemptedSubmit = false;
    this.addUpdate = {};
    this.editingUpdateId = null;
    this.emailingUpdateId = null;
  }

  isAddUpdateValid(): boolean {
    return !!(
      this.addUpdate.label?.trim() ||
      this.addUpdate.internalNotes?.trim() ||
      this.addUpdate.clientNotes?.trim()
    );
  }

  saveUpdate(): void {
    if (!this.displayNote?.id || this.isSavingUpdate) return;
    this.updateAttemptedSubmit = true;
    if (!this.isAddUpdateValid()) {
      this.toastr.warning("Please add notes to the update");
      return;
    }
    this.savePendingUpdate();
  }

  /** Persists whatever is in the add/edit update form (create or edit). */
  private savePendingUpdate(): Promise<boolean> {
    if (!this.displayNote?.id || this.isSavingUpdate) return Promise.resolve(true);
    if (!this.showAddUpdate) return Promise.resolve(true);
    if (!this.isAddUpdateValid()) return Promise.resolve(true);

    const noteId = this.displayNote.id;
    return new Promise<boolean>((resolve) => {
      const isEditing = !!this.editingUpdateId;
      this.isSavingUpdate = true;
      this.http
        .post("api/client/notes/updates", {
          id: isEditing ? (this.editingUpdateId as string) : undefined,
          clientNoteId: noteId,
          label: this.addUpdate.label?.trim() || undefined,
          internalNotes: this.addUpdate.internalNotes?.trim() || undefined,
          clientNotes: this.addUpdate.clientNotes?.trim() || undefined,
        })
        .subscribe({
          next: (saved: any) => {
            this.isSavingUpdate = false;
            const updated = saved as ClientNoteUpdate;
            if (isEditing) {
              const updates = this.displayNote?.updates || [];
              const idx = updates.findIndex((u) => u.id === updated.id);
              if (idx !== -1) updates[idx] = updated;
              this.toastr.success("Update saved");
            } else {
              if (this.displayNote) {
                this.displayNote.updates = this.displayNote.updates || [];
                this.displayNote.updates.push(updated);
              }
              this.toastr.success("Update added");
            }
            this.sortUpdates();
            this.updateAttemptedSubmit = false;
            this.addUpdate = {};
            this.showAddUpdate = false;
            this.editingUpdateId = null;
            resolve(true);
          },
          error: () => {
            this.isSavingUpdate = false;
            this.toastr.error("Failed to save update");
            resolve(false);
          },
        });
    });
  }

  deleteUpdate(update: ClientNoteUpdate): void {
    if (!update?.id) return;
    if (!window.confirm("Delete this status update? This cannot be undone.")) return;
    this.http.delete(`api/client/notes/updates/${update.id}`).subscribe({
      next: () => {
        if (this.displayNote) {
          this.displayNote.updates = (this.displayNote.updates || []).filter(
            (u) => u.id !== update.id
          );
        }
        this.toastr.success("Update deleted");
      },
      error: () => this.toastr.error("Failed to delete update"),
    });
  }

  formatUpdateDate(date: any): string {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  // ── Email the client ─────────────────────────────────────────────

  /**
   * Loads the current user's email signature once so every email this modal opens
   * carries it (and so the signature editor can show / update it).
   */
  private loadEmailSignature(): void {
    if (this.signatureLoaded) return;
    this.signatureLoaded = true;
    this.configService.getCurrentUserEmailSignature().subscribe({
      next: (signature: string) => {
        this.userEmailSignature = signature || "";
      },
      error: () => {},
    });
  }

  /** Loads the configured default CC so the compose dialog can pre-fill it. */
  private loadDefaultCc(): void {
    if (this.defaultCcLoaded) return;
    this.defaultCcLoaded = true;
    this.configService.getDriverNotesCcEmail().subscribe({
      next: (ccEmail: string) => {
        this.driverNotesCcEmail = ccEmail;
      },
      error: () => {},
    });
  }

  /** Appends the sender signature to a message (signature alone when the message is blank). */
  private withSenderSignature(body: string): string {
    const signature = this.userEmailSignature?.trim();
    const message = (body ?? "").trim();
    if (!signature) return message;
    return message ? `${message}\n\n${signature}` : signature;
  }

  /** Stores the message without the signature so the editor can re-render, and returns the signed body. */
  private setEmailMessage(message: string): string {
    this.emailBaseBody = message ?? "";
    return this.withSenderSignature(this.emailBaseBody);
  }

  toggleSignatureEditor(): void {
    this.showSignatureEditor = !this.showSignatureEditor;
    if (this.showSignatureEditor) {
      this.signatureEditText = this.userEmailSignature || "";
      this.signatureSaveMessage = "";
      this.signatureSaveError = false;
    }
  }

  saveSignature(): void {
    if (this.savingSignature) return;
    this.savingSignature = true;
    this.signatureSaveMessage = "";
    this.signatureSaveError = false;
    this.configService.updateCurrentUserEmailSignature(this.signatureEditText ?? "").subscribe({
      next: (saved: string) => {
        this.savingSignature = false;
        this.userEmailSignature = saved || "";
        // Refresh the open email so the signature change shows immediately.
        this.emailPreview.body = this.withSenderSignature(this.emailBaseBody);
        this.showSignatureEditor = false;
        this.signatureSaveMessage = "";
      },
      error: () => {
        this.savingSignature = false;
        this.signatureSaveMessage = "Could not save signature. Please try again.";
        this.signatureSaveError = true;
      },
    });
  }

  /**
   * Opens the compose dialog for emailing this note to the client, defaulting the
   * recipient to the client's own email address (loaded on demand - the note itself
   * only carries the client id / name).
   */
  openEmailClient(): void {
    if (!this.displayNote?.id) return;

    this.emailingUpdateId = null;
    const clientName = this.displayNote.clientName || "Client";
    this.emailPreview = {
      to: "",
      ccEmails: this.driverNotesCcEmail,
      subject: `General Note - ${clientName}`,
      body: this.setEmailMessage(
        `We wanted to share the following note with you:\n\n${this.displayNote.note ?? ""}`
      ),
    };
    this.showSignatureEditor = false;
    this.emailValidationError = "";
    this.showEmailCompose = true;

    this.clientServiceRef.getClientInfo(this.displayNote.clientId).subscribe({
      next: (client: any) => {
        this.emailPreview.to = client?.email || client?.invoiceEmail || "";
      },
      error: () => {},
    });
  }

  /** Emails a single status update to the client; sending marks the update as emailed. */
  emailUpdateToClient(update: ClientNoteUpdate): void {
    if (!update?.id || !this.displayNote?.id) return;

    this.emailingUpdateId = update.id;
    const clientName = this.displayNote.clientName || "Client";
    const label = update.label?.trim() || "Update";

    // The message is JUST the update's public notes - no greeting or boilerplate copy.
    this.emailPreview = {
      to: "",
      ccEmails: this.driverNotesCcEmail,
      subject: `${this.displayNote.type} ${label} - ${clientName}`,
      body: this.setEmailMessage(update.clientNotes || ""),
    };
    this.showSignatureEditor = false;
    this.emailValidationError = "";
    this.showEmailCompose = true;

    this.clientServiceRef.getClientInfo(this.displayNote.clientId).subscribe({
      next: (client: any) => {
        this.emailPreview.to = client?.email || client?.invoiceEmail || "";
      },
      error: () => {},
    });
  }

  cancelEmailCompose(): void {
    this.showEmailCompose = false;
    this.emailValidationError = "";
    this.emailingUpdateId = null;
  }

  confirmSendEmail(): void {
    if (!this.emailPreview.to?.trim()) {
      this.emailValidationError = "Recipient email is required.";
      return;
    }

    this.emailValidationError = "";
    this.emailSending = true;

    const updateId = this.emailingUpdateId;
    this.http
      .post("api/client/notes/sendEmail", {
        to: this.emailPreview.to.trim(),
        ccEmails: this.emailPreview.ccEmails ?? "",
        bccEmails: null,
        subject: this.emailPreview.subject,
        body: this.emailPreview.body,
        clientNoteUpdateId: updateId,
      })
      .subscribe({
        next: (response: any) => {
          this.emailSending = false;
          this.showEmailCompose = false;
          this.emailingUpdateId = null;

          // Reflect the emailed stamp on the update that was just sent.
          if (updateId && response?.update && this.displayNote?.updates) {
            const idx = this.displayNote.updates.findIndex((u) => u.id === updateId);
            if (idx !== -1) {
              Object.assign(this.displayNote.updates[idx], response.update);
            }
          }

          this.toastr.success("Email sent successfully.");
        },
        error: (err: any) => {
          this.emailSending = false;
          this.toastr.error(err?.error?.error || "Failed to send email.");
        },
      });
  }
}
